/**
 * Warm a route's lazy chunk before the user clicks it.
 *
 * THE PROBLEM THIS SOLVES
 * -----------------------
 * Every in-app page is `React.lazy`, so clicking a sidebar item starts a
 * SERIAL chain: download the chunk, mount the component, and only THEN can the
 * component fire the API call it needs. Measured on production:
 *
 *     /pricing   chunk 232ms  ->  then /api/sub/plans 166ms   = 431ms
 *     /trends    chunk 232ms  ->  then /api/trends    174ms   = 430ms
 *
 * The two legs cannot overlap, because the request that fetches the data lives
 * inside the code being downloaded. React itself is not the bottleneck — the
 * route shell renders in ~50ms once the chunk is present.
 *
 * Prefetching on hover/focus removes the first leg for anyone who moves a
 * mouse toward a link, which is the overwhelming majority of navigations. The
 * chunk is already in memory by the time the click lands, so the navigation
 * costs only its API call.
 *
 * WHY A SEPARATE MAP, AND HOW IT STAYS HONEST
 * -------------------------------------------
 * webpack keys chunks by module path, so `import("../pages/DashboardPage")`
 * here resolves to the *same* chunk `App.js` created with `lazy(() =>
 * import("./pages/DashboardPage"))`. Calling it twice does not double-fetch.
 *
 * The cost is that the paths are written in two places and could drift. A
 * stale entry here fails silently — the prefetch just misses and navigation
 * returns to today's speed. `src/lib/__tests__` has no runner in this repo, so
 * the guard is `scripts/check-route-prefetch.js`, which asserts every sidebar
 * destination has an entry and every entry points at a file that exists. Run
 * it in CI or before a deploy.
 */

// path -> the SAME dynamic import App.js uses for that route.
const ROUTE_IMPORTS = {
  "/dashboard": () => import("../pages/DashboardPage"),
  "/posts": () => import("../pages/PostsReelsPage"),
  "/automation": () => import("../pages/AutomationPage"),
  "/contacts": () => import("../pages/ContactsPage"),
  "/products": () => import("../pages/ProductsPage"),
  "/bio": () => import("../pages/BioEditorPage"),
  "/analytics": () => import("../pages/analytics/AnalyticsV3"),
  "/collabs": () => import("../pages/CreatorMarketplacePage"),
  "/leads": () => import("../pages/LeadsPage"),
  "/ab-testing": () => import("../pages/ABTestingPage"),
  "/ai-writer": () => import("../pages/AIWriterPage"),
  "/growth-agent": () => import("../pages/GrowthAgentPage"),
  "/trends": () => import("../pages/TrendsPage"),
  "/inbox": () => import("../pages/InboxPage"),
  "/comments": () => import("../pages/CommentsPage"),
  "/notifications": () => import("../pages/NotificationsPage"),
  "/settings": () => import("../pages/SettingsPage"),
  "/pricing": () => import("../pages/PricingPage"),
};

/** Paths already requested this session — import() dedupes, this avoids the churn. */
const warmed = new Set();

/**
 * Kick off the chunk download for `path`. Safe to call repeatedly and from
 * event handlers: it never throws, never blocks, and a failure is a no-op
 * (the real navigation will retry the import and surface any error there).
 */
export function prefetchRoute(path) {
  if (!path || warmed.has(path)) return;
  const load = ROUTE_IMPORTS[path];
  if (!load) return;
  warmed.add(path);
  try {
    load().catch(() => {
      // Offline, or the chunk 404s after a deploy replaced it. Let the next
      // real navigation deal with it rather than failing a hover.
      warmed.delete(path);
    });
  } catch {
    warmed.delete(path);
  }
}

/**
 * Handlers to spread onto a nav control:  {...prefetchHandlers("/dashboard")}
 *
 * `onMouseEnter` covers pointer users, `onFocus` covers keyboard users, and
 * `onTouchStart` covers mobile — where there is no hover, but the gap between
 * touch-down and the click event is still enough to start the download.
 */
export function prefetchHandlers(path) {
  const fire = () => prefetchRoute(path);
  return { onMouseEnter: fire, onFocus: fire, onTouchStart: fire };
}

export const PREFETCHABLE_ROUTES = Object.keys(ROUTE_IMPORTS);
